/**
 * Job contracts (ARCHITECTURE §8.4, §10.3, §11, grafts G-5/G-7/G-12).
 *
 * - `jobs` rows are the dashboard-facing mirror of runtime queue state
 *   (source of truth stays `.llm-wiki/*.json` inside the project dir).
 * - `job_events` rows are the durable per-job audit/log/progress record
 *   that outlives the short-retention SSE ring (graft G-7).
 * - CLI-runner payloads type wiki-runnerd's HTTP+SSE API
 *   (`POST /runs`, `GET /runs/{id}/events`, `DELETE /runs/{id}`) and the
 *   unified adapter event union from cli-runner-research §4.3.
 */
import { z } from "zod";
import { DuplicateGroupSchema, IsoDateTimeSchema, JsonObjectSchema, JsonValueSchema, UuidSchema, } from "./core.js";
// ─── Job kinds & statuses ────────────────────────────────────────────────
export const JobKindSchema = z.enum([
    "ingest",
    "dedup",
    "dedup-scan",
    "embedding-reindex",
    "research",
    "scheduled-import",
    "git-sync",
    "file-sync",
    "cli-run",
    "lint",
    "chat",
]);
export const JobStatusSchema = z.enum([
    "pending",
    "processing",
    "done",
    "failed",
    "cancelled",
]);
// ─── Job payloads (per kind) ─────────────────────────────────────────────
export const IngestJobPayloadSchema = z.object({
    kind: z.literal("ingest"),
    /** Relative to project root, e.g. `raw/sources/folder/file.pdf`. */
    sourcePath: z.string(),
    /** e.g. `AI-Research > papers` or `""`. */
    folderContext: z.string(),
    /** Engine ingest-queue task id this job mirrors. */
    taskId: z.string(),
});
export const DedupJobPayloadSchema = z.object({
    kind: z.literal("dedup"),
    group: DuplicateGroupSchema,
    canonicalSlug: z.string(),
    taskId: z.string(),
});
/**
 * Whole-project duplicate detection run (F-036…F-042 scan stages). The
 * scan is long-running (LLM group detection over every entity summary),
 * so `dedup.startScan` returns a jobId immediately; the confirmed groups
 * land in the job summary and a terminal `result` job_event.
 */
export const DedupScanJobPayloadSchema = z.object({
    kind: z.literal("dedup-scan"),
    /** Runtime-local scan id the deterministic jobId was derived from. */
    scanId: z.string(),
    groupCount: z.number().int().nonnegative().optional(),
    groups: z.array(DuplicateGroupSchema).optional(),
    error: z.string().optional(),
});
/**
 * Atomic re-index of the embedding store (F-091 re-index-all entry,
 * desktop Settings → Embedding). Progress arrives as `progress`
 * job_events `{current, total}`.
 */
export const EmbeddingReindexJobPayloadSchema = z.object({
    kind: z.literal("embedding-reindex"),
    reindexId: z.string(),
    clearExisting: z.boolean().optional(),
    /** Pages embedded (terminal). */
    embedded: z.number().int().nonnegative().optional(),
    error: z.string().optional(),
});
export const ResearchJobPayloadSchema = z.object({
    kind: z.literal("research"),
    topic: z.string(),
    searchQueries: z.array(z.string()).optional(),
    taskId: z.string(),
});
export const ScheduledImportJobPayloadSchema = z.object({
    kind: z.literal("scheduled-import"),
    /** Watched dir (relative to project root, or inbound drop dir). */
    path: z.string(),
});
export const GitSyncJobPayloadSchema = z.object({
    kind: z.literal("git-sync"),
    /** Connector repo id being synced. */
    repoId: z.string(),
    /** HEAD commit after sync (terminal). */
    commit: z.string().optional(),
    filesAdded: z.number().int().nonnegative().optional(),
    filesUpdated: z.number().int().nonnegative().optional(),
    filesRemoved: z.number().int().nonnegative().optional(),
    error: z.string().optional(),
});
export const FileSyncJobPayloadSchema = z.object({
    kind: z.literal("file-sync"),
    /** file-change-queue task ids processed by this batch. */
    taskIds: z.array(z.string()),
});
export const LintJobPayloadSchema = z.object({
    kind: z.literal("lint"),
    /** Empty = whole wiki. */
    pages: z.array(z.string()).default([]),
});
export const ChatJobPayloadSchema = z.object({
    kind: z.literal("chat"),
    conversationId: z.string(),
    streamId: z.string(),
});
export const CliRunJobPayloadSchema = z.object({
    kind: z.literal("cli-run"),
    /** cli_runs row id (runnerd run id). */
    runId: UuidSchema,
});
export const JobPayloadSchema = z.discriminatedUnion("kind", [
    IngestJobPayloadSchema,
    DedupJobPayloadSchema,
    DedupScanJobPayloadSchema,
    EmbeddingReindexJobPayloadSchema,
    ResearchJobPayloadSchema,
    ScheduledImportJobPayloadSchema,
    GitSyncJobPayloadSchema,
    FileSyncJobPayloadSchema,
    LintJobPayloadSchema,
    ChatJobPayloadSchema,
    CliRunJobPayloadSchema,
]);
// ─── jobs table row (§8.4) ───────────────────────────────────────────────
export const JobRowSchema = z.object({
    id: UuidSchema,
    projectId: UuidSchema,
    kind: JobKindSchema,
    status: JobStatusSchema,
    /**
     * Kind-specific mirror payload + human-readable progress summary.
     * The typed part parses with `JobPayloadSchema`; extra summary fields
     * (title, counts, error, filesWritten…) are free-form JSON.
     */
    summary: JsonObjectSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
});
/** Upsert shape used on the runtime→gateway uplink (id generated once). */
export const JobUpsertSchema = JobRowSchema.omit({
    createdAt: true,
    updatedAt: true,
}).extend({
    createdAt: IsoDateTimeSchema.optional(),
});
// ─── job_events rows (§10.3, graft G-7) ──────────────────────────────────
export const JobEventTypeSchema = z.enum([
    "status", //           status transition {from, to}
    "progress", //          {current, total?, message?}
    "log", //               {level, message}
    "error", //             {message, retryCount?}
    "synthesis-delta", //   deep-research progressive synthesis {delta}
    "result", //            terminal payload {…}
]);
export const JobEventRowSchema = z.object({
    jobId: UuidSchema,
    /** bigserial, monotonic per job — the SSE replay cursor. */
    seq: z.number().int().positive(),
    ts: IsoDateTimeSchema,
    type: JobEventTypeSchema,
    data: JsonValueSchema,
});
/** Append shape on the uplink (seq/ts assigned by Postgres). */
export const JobEventAppendSchema = JobEventRowSchema.omit({
    seq: true,
    ts: true,
});
// Typed `data` payloads for the common job-event types.
export const JobProgressDataSchema = z.object({
    current: z.number().int().nonnegative(),
    total: z.number().int().nonnegative().optional(),
    message: z.string().optional(),
});
export const JobLogDataSchema = z.object({
    level: z.enum(["debug", "info", "warn", "error"]),
    message: z.string(),
});
export const JobErrorDataSchema = z.object({
    message: z.string(),
    retryCount: z.number().int().nonnegative().optional(),
});
export const JobStatusDataSchema = z.object({
    from: JobStatusSchema.optional(),
    to: JobStatusSchema,
});
// ─── Tenant quotas (§8.4, graft G-5) ─────────────────────────────────────
export const TenantQuotasSchema = z.object({
    tenantId: UuidSchema,
    maxConcurrentJobs: z.number().int().min(0),
    maxCliMinutesDay: z.number().int().min(0),
    maxCostUsdDay: z.number().min(0),
});
// ─── CLI runner: run request (wiki-runnerd POST /runs) ───────────────────
export const CliKindSchema = z.enum([
    "claude",
    "codex",
    "gemini",
    "opencode",
    "openrouter",
]);
/**
 * Chat message forwarded to a CLI adapter. Content stays the engine's
 * block-array-or-string union; adapters own the per-CLI normalization
 * (e.g. claude-cli-transport folds system messages into the first user
 * turn and maps image blocks to Anthropic base64 shape).
 */
export const CliMessageContentBlockSchema = z.union([
    z.object({ type: z.literal("text"), text: z.string() }),
    z.object({
        type: z.literal("image"),
        mediaType: z.string(),
        dataBase64: z.string(),
    }),
]);
export const CliMessageSchema = z.object({
    role: z.enum(["system", "user", "assistant"]),
    content: z.union([z.string(), z.array(CliMessageContentBlockSchema)]),
});
export const CliRunRequestSchema = z.object({
    tenantId: UuidSchema,
    projectId: UuidSchema,
    /**
     * Optional: runs started by a project runtime through the desktop-compat
     * `/cmd/*_cli_spawn` seam carry no end-user identity (the engine process
     * is per-(tenant,project), not per-user). cli_runs.user_id is nullable.
     */
    userId: UuidSchema.optional(),
    cli: CliKindSchema,
    model: z.string(),
    messages: z.array(CliMessageSchema).min(1),
    /**
     * Pre-built single-string prompt (desktop-compat seam, F-075): the
     * vendored codex/gemini/opencode transports build the role-tagged
     * prompt (`<USER>…</USER>` with escapePromptContent) in the engine and
     * ship it verbatim. When present, single-prompt adapters pipe THIS to
     * stdin instead of re-deriving it from `messages` — re-deriving would
     * double-escape the role tags and diverge from the desktop wire bytes.
     * `messages` still carries the semantic history (metering/resume).
     */
    rawPrompt: z.string().optional(),
    /** Resume an adapter session (claude `--resume`); optional. */
    sessionId: z.string().optional(),
    /** Engine stream id — echoed in events so the transport shim can route. */
    streamId: z.string(),
    /**
     * Read-only wiki snapshot the sandbox receives (graft G-2). The
     * runnerd resolves it to a tar/overlay of `<project>/wiki/`; results
     * come back as events and are applied by the project runtime.
     */
    workspaceSnapshot: z
        .object({
        includeWiki: z.boolean().default(true),
        includeSources: z.boolean().default(false),
    })
        .default({ includeWiki: true, includeSources: false }),
    /** Local CLI isolation flags (F-076 posture; claude `--bare` etc.). */
    isolateLocalConfig: z.boolean().default(true),
    /** Wall-clock deadline minutes, clamped 1..240 (graft G-5). */
    timeoutMinutes: z.number().int().min(1).max(240).default(10),
    /** Turn cap forwarded to the CLI (`--max-turns` etc.), if supported. */
    maxTurns: z.number().int().min(1).optional(),
    /**
     * Tool allowlist forwarded to the CLI when supported
     * (claude `--allowedTools "Read,Edit,Bash"`). Omitted = CLI default.
     */
    allowedTools: z.array(z.string().min(1)).optional(),
});
export const CliRunAcceptedSchema = z.object({
    ok: z.literal(true),
    runId: UuidSchema,
    jobId: UuidSchema,
});
// ─── CLI runner: unified adapter event union (research §4.3) ─────────────
export const CliUsageSchema = z.object({
    inputTokens: z.number().int().nonnegative().optional(),
    outputTokens: z.number().int().nonnegative().optional(),
    cacheReadTokens: z.number().int().nonnegative().optional(),
    cacheWriteTokens: z.number().int().nonnegative().optional(),
    /** claude `total_cost_usd`; OpenRouter usage cost. */
    costUsd: z.number().nonnegative().optional(),
    /** OpenRouter `X-Generation-Id`. */
    generationId: z.string().optional(),
});
/**
 * One file the sandboxed CLI created/modified/deleted inside its
 * throwaway workspace snapshot (graft G-2). Results are NEVER written
 * back to the live project volume by runnerd — they travel as
 * `file_result` events and are applied by the owning project runtime
 * under its own mutex + fencing check (§11.2, ADR-004).
 */
export const CliFileResultSchema = z.object({
    /** Forward-slash path relative to the workspace root (e.g. `wiki/foo.md`). */
    path: z.string().min(1),
    op: z.enum(["created", "modified", "deleted"]),
    /** Absent for `deleted` and when the per-run content budget was hit. */
    contentBase64: z.string().optional(),
    /** True when content was omitted/cut because it exceeded the cap. */
    truncated: z.boolean().optional(),
});
export const RunnerEventSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("init"),
        runId: UuidSchema,
        streamId: z.string(),
        sessionId: z.string().optional(),
        cli: CliKindSchema,
        model: z.string(),
    }),
    z.object({
        type: z.literal("text_delta"),
        runId: UuidSchema,
        streamId: z.string(),
        text: z.string(),
    }),
    z.object({
        type: z.literal("reasoning"),
        runId: UuidSchema,
        streamId: z.string(),
        text: z.string(),
    }),
    z.object({
        type: z.literal("tool_call"),
        runId: UuidSchema,
        streamId: z.string(),
        name: z.string(),
        input: JsonValueSchema.optional(),
    }),
    z.object({
        type: z.literal("usage"),
        runId: UuidSchema,
        streamId: z.string(),
        usage: CliUsageSchema,
    }),
    z.object({
        type: z.literal("error"),
        runId: UuidSchema,
        streamId: z.string(),
        message: z.string(),
        /** Adapter exit taxonomy (e.g. `not_installed`, `auth`, `timeout`,
         *  `killed`, `output_cap`, `nonzero_exit`, `parse`). */
        code: z.string().optional(),
        exitCode: z.number().int().nullable().optional(),
        stderrTail: z.string().optional(),
    }),
    z.object({
        type: z.literal("file_result"),
        runId: UuidSchema,
        streamId: z.string(),
        files: z.array(CliFileResultSchema),
    }),
    z.object({
        type: z.literal("done"),
        runId: UuidSchema,
        streamId: z.string(),
        exitCode: z.number().int().nullable(),
        /** Full final assistant text (silent-success detection input). */
        finalText: z.string().optional(),
        usage: CliUsageSchema.optional(),
    }),
]);
/** Canonical alias — “CliRunEvent” is the name used by ARCHITECTURE §11. */
export const CliRunEventSchema = RunnerEventSchema;
// ─── cli_runs metering row (§8.4, §11.5) ─────────────────────────────────
export const CliRunStatusSchema = z.enum([
    "queued",
    "running",
    "done",
    "failed",
    "cancelled",
    "timeout",
]);
export const CliRunRowSchema = z.object({
    id: UuidSchema,
    tenantId: UuidSchema,
    projectId: UuidSchema,
    userId: UuidSchema,
    cli: CliKindSchema,
    model: z.string(),
    status: CliRunStatusSchema,
    startedAt: IsoDateTimeSchema.nullable(),
    finishedAt: IsoDateTimeSchema.nullable(),
    exitCode: z.number().int().nullable(),
    usage: CliUsageSchema.nullable(),
    costUsd: z.number().nullable(),
    transcriptPath: z.string().nullable(),
});
// ─── Runnerd admission errors (graft G-5) ────────────────────────────────
export const QuotaDeniedSchema = z.object({
    ok: z.literal(false),
    error: z.string(),
    code: z.literal("quota_exceeded"),
    details: z.object({
        quota: z.enum([
            "max_concurrent_jobs",
            "max_cli_minutes_day",
            "max_cost_usd_day",
        ]),
        limit: z.number(),
        current: z.number(),
    }),
});
//# sourceMappingURL=jobs.js.map